export type Lang = 'en' | 'hu';

const locales: Record<Lang, string> = { hu: 'hu-HU', en: 'en-GB' };

export const formatDate = (date: Date | undefined | null, lang: Lang, withTime = false): string => {
  if (!date || isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locales[lang], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(date);
};

export const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${Math.round((bytes / Math.pow(k, i)) * 10) / 10} ${sizes[i]}`;
};

export const normalizeEmail = (email: string): string => email.trim().toLowerCase();

export const isValidEmail = (email: string): boolean =>
  /^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/.test(email.trim());

/** Best-effort public IP lookup for the download log; never blocks for long. */
export const fetchPublicIp = async (timeoutMs = 2500): Promise<string | undefined> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch('https://api.ipify.org?format=json', { signal: controller.signal });
    const data = await res.json();
    return typeof data.ip === 'string' ? data.ip : undefined;
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
};

/** Opens a file URL in a way that survives popup blockers after async work. */
export const startFileDownload = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const toDate = (value: any): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  return undefined;
};
